import React, {useState, useEffect} from 'react';
import {Box, Stack, Grid} from '@mui/material';
import {t} from 'i18next';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {useFieldData} from '../../../../../../hooks/session-search/useSessionSearch';
import {FieldTypeProps} from '../FieldTypeProps';
import './SavedSearchResultsField.scss';
import '../FieldTypes.scss';
import {
  disableEmptyParents,
  findItemById,
} from '../../field-types/fieldTypesUtils';
import {formatSavedSearchDisplayLabel} from '../../../../utilities/savedSearchCriteria';
import {SAVED_SEARCH_NAME_CONVERSION_THRESHOLD} from '../../../../../../config/env';

export interface SavedSearchesFieldTypeProps extends Omit<
  FieldTypeProps,
  'onCriteriaChaged'
> {
  onCriteriaChaged?: (value: string[], displayValue?: string) => void;
}

const SavedSearchResultsFieldType: React.FC<SavedSearchesFieldTypeProps> = ({
  type = 'SAVEDSEARCHES',
  onCriteriaChaged,
  onValidated,
}) => {
  const {data} = useFieldData(type);
  const [selectedSavedSearch, setSelectedSavedSearch] = useState<string[]>([]);
  const [treeItems, setTreeItems] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const makeLabelWithID = (label: string) => {
    const result = label.replace(/\((\d+)\)$/, '(ID: $1)');
    return result;
  };

  const convertToTree = (items: any[]) => {
    const map: any = {};
    const roots: any[] = [];
    items.forEach((item) => {
      const label = makeLabelWithID(item.text);
      map[item.id] = {
        id: item.id,
        label,
        isGroup: item.isGroup,
        parent: item.parent,
        fileType: item.isGroup ? 'folder' : 'tag',
        children: [],
      };
    });
    // build the tree
    items.forEach((item) => {
      if (item.parent && item.parent !== '#') {
        map[item.parent]?.children.push(map[item.id]);
      } else {
        roots.push(map[item.id]);
      }
    });

    return disableEmptyParents(roots);
  };

  const filterTreeBySavedSearchName = (nodes: any[], keyword: string) =>
    nodes.reduce<any[]>((filteredNodes, node) => {
      const filteredChildren = filterTreeBySavedSearchName(
        node.children ?? [],
        keyword
      );
      const isMatchingSavedSearch =
        !node.isGroup &&
        node.label.toLowerCase().includes(keyword.toLowerCase());

      if (!isMatchingSavedSearch && filteredChildren.length === 0) {
        return filteredNodes;
      }

      return [
        ...filteredNodes,
        {
          ...node,
          children: filteredChildren,
        },
      ];
    }, []);

  const getExpandedFolderIds = (nodes: any[]): string[] =>
    nodes.flatMap((node) => [
      ...(node.isGroup && node.children?.length ? [node.id] : []),
      ...getExpandedFolderIds(node.children ?? []),
    ]);
  // Handle tree selection
  useEffect(() => {
    if (!data?.workProductItemsDetails) return;
    const tree = convertToTree(data.workProductItemsDetails);
    setTreeItems(tree);
  }, [data]);

  useEffect(() => {
    if (isSearchLoading) {
      setIsSearchLoading(false);
    }
  }, [isSearchLoading, searchKeyword]);

  const validateForm = (selected: string[]) => {
    const valid = selected.length > 0;
    onValidated?.(valid);
  };

  const handleSearchKeyEnter = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    const submittedKeyword = searchInput.trim();
    setSearchKeyword(submittedKeyword);
    setIsSearchLoading(Boolean(submittedKeyword));
  };

  const handleTreeChange = (selectedItems: string[] | null) => {
    if (!selectedItems) return;
    setSelectedSavedSearch(selectedItems);
    validateForm(selectedItems);
    const selectedData = selectedItems
      .map((id) => findItemById(treeItems, id))
      .filter((item) => item && !item.isGroup && item.parent !== '#')
      .map((item) => item!);
    const selectedIds = selectedData.map((item) => item.id);
    const displayValue =
      selectedIds.length > 0 &&
      selectedIds.length < SAVED_SEARCH_NAME_CONVERSION_THRESHOLD
        ? `savedSearches: [ ${selectedData
            .map(
              (item) =>
                `"${formatSavedSearchDisplayLabel(item.label, item.id)}"`
            )
            .join(', ')} ]`
        : undefined;
    onCriteriaChaged?.(selectedIds, displayValue || undefined);
  };

  const filteredTreeItems = searchKeyword
    ? filterTreeBySavedSearchName(treeItems, searchKeyword)
    : treeItems;

  return (
    <Grid
      container
      className={`savedSearch-panel ${treeItems?.length ? 'has-data' : 'no-data'}`}
    >
      <Grid size={12}>
        <Stack spacing={2}>
          <Box className="savedSearch-searchbar">
            <ArrTextField
              state="enabled"
              sizeVariant="medium"
              className="search-input"
              placeholder={t(
                'sessionSearch.queryList.workProduct.savedSearchResultsDrawer.search'
              )}
              aria-label={t(
                'sessionSearch.queryList.workProduct.savedSearchResultsDrawer.search'
              )}
              dataTestId="saved-search-results-search-input"
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
                if (event.target.value.trim() === '') {
                  setSearchKeyword('');
                  setIsSearchLoading(false);
                }
              }}
              onKeyDown={handleSearchKeyEnter}
              onClear={() => {
                setSearchInput('');
                setSearchKeyword('');
                setIsSearchLoading(false);
              }}
              type="search"
              slotProps={{inputLabel: {shrink: false}}}
            />
          </Box>
          <Box className="savedSearch-results">
            <ArrLoadingSpinner show={isSearchLoading} size={24} />
            <ArrTreeView
              key={searchKeyword || 'all-saved-searches'}
              itemsList={filteredTreeItems}
              isChildrenIndendation={true}
              handleNoResultMessage={t(
                'sessionSearch.queryList.workProduct.savedSearchResultsDrawer.noSavedSearchesFound'
              )}
              allowGlobalSelect
              multipleSelection
              isShowCheckbox={true}
              setSelectedItem={handleTreeChange}
              treeSelectedItems={selectedSavedSearch}
              isShowFileType={false}
              defaultExpandedIds={
                searchKeyword
                  ? getExpandedFolderIds(filteredTreeItems)
                  : undefined
              }
            />
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default SavedSearchResultsFieldType;
